/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  TextField,
  useMediaQuery,
  useTheme,
  Typography,
  Container,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { useAppDispatch } from "@store/store";
import { TerminosCondiciones } from "@components/TerminosCondiciones";
import {
  PASSWORD,
  HAS_SPECIAL_CHARACTERS,
  HAS_NUMBERS,
  HAS_UPPERCASE,
  HAS_LOWERCASE,
  HAS_WHITESPACE,
  EMAIL,
} from "@utils/expreciones";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import * as Sentry from "@sentry/react";
import { confirmSignIn, getCurrentUser } from "aws-amplify/auth";
import { useLazyGetSessionInfoQuery } from "@api/session";

const ActualizacionContrasena = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const navigationState = routerLocation.state as { from?: unknown; email?: string } | null;
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const interfaceConfig = useInterfaceConfig();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [triggerGetSessionInfo, sessionInfoResult] = useLazyGetSessionInfoQuery();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: {
      password: "",
      email: "",
    },
    validationSchema: yup.object().shape({
      email: yup
        .string()
        .required("Campo requerido")
        .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
      password: yup.string().required("Campo requerido").matches(PASSWORD, "Formato Incorrecto"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        setIsLoading(true);
        if (values.password !== formik.values.password) {
          return;
        }

        await confirmSignIn({
          challengeResponse: values.password,
        });

        const currentUser = await getCurrentUser();

        if (!currentUser?.userId) {
          console.warn("No se encontró userId en currentUser");
          return;
        }

        await triggerGetSessionInfo(currentUser.userId).unwrap();
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "ActualizacionContrasena-onSubmit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
        setIsLoading(false);
      }
    },
  });

  const isLengthValid = formik.values.password.length >= 8 && formik.values.password.length <= 20;
  const hasNumber = HAS_NUMBERS.test(formik.values.password);
  const hasSpecialChar = HAS_SPECIAL_CHARACTERS.test(formik.values.password);
  const hasUpperCase = HAS_UPPERCASE.test(formik.values.password);
  const hasLowerCase = HAS_LOWERCASE.test(formik.values.password);
  const hasWhitespace = HAS_WHITESPACE.test(formik.values.password);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onChangeEmail = (value: string) => {
    const low = value.toLowerCase().trim();
    formik.setFieldValue("email", low);
  };

  useEffect(() => {
    if (sessionInfoResult.isSuccess) {
      navigate(sessionInfoResult.data.data.current_screen.url, {
        state: { from: navigationState?.from ?? routerLocation },
      });
    }
  }, [sessionInfoResult]);

  useEffect(() => {
    const emailFromState = navigationState?.email;
    const fallbackEmail = localStorage.getItem("email");
    const emailToSet = (typeof emailFromState === "string" && emailFromState.trim() ? emailFromState : fallbackEmail)
      ?.toLowerCase()
      .trim();

    if (typeof emailToSet === "string" && emailToSet) {
      formik.setFieldValue("email", emailToSet);
    }
  }, []);

  return (
    <Grid container height={"100%"}>
      <Grid item xs={12} md={8} mt={4}>
        <Box
          sx={{
            mt: isMobileOrTablet ? 1 : 2,
            mx: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={theme?.palette?.fontWeights?.bold}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pm?.crear_cuenta?.titulo || "Te damos la bienvenida"}
          </Typography>

          <Typography
            mt={isMobileOrTablet ? 2 : 3}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
          >
            Crea una contraseña segura para tu cuenta {interfaceConfig?.name.toUpperCase()} powered by Lendia.
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Typography variant={"body1"} color={theme?.palette?.primary?.dark} textAlign={"center"} fontWeight={500}>
                Ingresa los datos para crear tu cuenta
              </Typography>
              <Typography
                variant={"body1"}
                color={theme?.palette?.primary?.main}
                textAlign={"center"}
                fontWeight={600}
              ></Typography>

              <Box>
                <TextField
                  id="email"
                  name="email"
                  label="Correo electrónico"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={(e) => onChangeEmail(e.target.value)}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  value={formik.values.email}
                />

                <TextField
                  id="password"
                  name="password"
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                          sx={{ color: theme?.palette?.primary?.main }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={formik.isSubmitting}
                />
              </Box>

              <Box mt={isMobileOrTablet ? 2 : 3}>
                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  fontWeight={500}
                  color={theme?.palette?.primary?.dark}
                >
                  Tu contraseña debe contener:
                </Typography>

                <Typography
                  mt={1}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={isLengthValid ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {isLengthValid ? <CheckIcon /> : <CloseIcon />} &nbsp;Entre 8 y 20 caracteres
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={hasNumber ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {hasNumber ? <CheckIcon /> : <CloseIcon />} &nbsp;Un carácter numérico
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={hasSpecialChar ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {hasSpecialChar ? <CheckIcon /> : <CloseIcon />} &nbsp;Un carácter especial
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={hasUpperCase ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {hasUpperCase ? <CheckIcon /> : <CloseIcon />} &nbsp;Una mayúscula
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={hasLowerCase ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {hasLowerCase ? <CheckIcon /> : <CloseIcon />} &nbsp;Una minúscula
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={!hasWhitespace ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {!hasWhitespace ? <CheckIcon /> : <CloseIcon />} &nbsp;Sin espacios en blanco
                </Typography>
              </Box>

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button
                  variant="default"
                  type="submit"
                  color="primary"
                  disabled={!formik.isValid || formik.isSubmitting || isLoading}
                >
                  Continuar
                </Button>
              </Box>
            </form>
            <Box sx={{ mt: 4, mb: 2 }}>
              <TerminosCondiciones />
            </Box>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default ActualizacionContrasena;
